import type {NextRouter} from 'next/router';

/**
 * Removes a single query param from the current URL, keeping the remaining
 * params, the hash and the scroll position.
 *
 * Replaces through the router rather than `window.history` so `router.query` and
 * `router.asPath` stay in sync — a stale router can otherwise re-serialize the
 * removed param back into the URL on the next shallow navigation.
 *
 * The displayed path is rebuilt from `window.location` instead of being taken
 * from `router.asPath`, because a page can be served under a public path that
 * differs from its file route. The route itself still comes from the router so
 * Next.js resolves the same page.
 */
export function removeUrlSearchParam(router: NextRouter, param: string): void {
    const params = new URLSearchParams(window.location.search);
    if (!params.has(param)) {
        return;
    }

    params.delete(param);
    const search = params.toString();
    const query = search ? `?${search}` : '';
    const asPath = `${window.location.pathname}${query}${window.location.hash}`;

    router.replace(`${router.pathname}${query}`, asPath, {shallow: true, scroll: false});
}
